import React, { useEffect, useState } from "react";
import { Table, Spin } from "antd";
import Breadcrumbs from "../../../components/Breadcrumbs";
import AttendanceEmployeeFilter from "../../../components/AttendanceEmployeeFilter";
import { useLazyGetEmployeeAttendanceQuery, useLazyGetAttendanceSummaryQuery } from "../../../services/attendance.service";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

const AttendanceEmployee = () => {
  const user = useSelector((state) => state.user.user);
  const [data, setData] = useState([]);
  const [statistics, setStatistics] = useState([]);
  const [todayActivity, setTodayActivity] = useState([]);
  const [currentAttendance, setCurrentAttendance] = useState(null);
  const [dateRange, setDateRange] = useState({
    start_date: dayjs().startOf('month').format('YYYY-MM-DD'),
    end_date: dayjs().format('YYYY-MM-DD')
  });

  const [getEmployeeAttendance, { isLoading: isLoadingAttendance }] = useLazyGetEmployeeAttendanceQuery();
  const [getAttendanceSummary, { isLoading: isLoadingSummary }] = useLazyGetAttendanceSummaryQuery();

  const handleFilterChange = (filters) => {
    setDateRange({
      start_date: filters.start_date,
      end_date: filters.end_date
    });
  };

  const userElements = data?.map((record, index) => ({
    key: index,
    id: record.id || index + 1,
    Date: record.date ? dayjs(record.date).format('DD MMM YYYY') : '-',
    PunchIn: record.first_in ? dayjs(record.first_in).format('hh:mm A') : '-',
    PunchOut: record.last_out ? dayjs(record.last_out).format('hh:mm A') : '-',
    Production: record.working_hours || '0h 0m',
    Break: record.break_hours || '0h 0m',
    Overtime: record.overtime_hours || '0h 0m',
  }));

  const columns = [
    {
      title: "#",
      dataIndex: "id",
      sorter: (a, b) => a.id.length - b.id.length,
    },
    {
      title: "Date",
      dataIndex: "Date",
      sorter: (a, b) => a.Date.length - b.Date.length,
    },
    {
      title: "PunchIn",
      dataIndex: "PunchIn",
      sorter: (a, b) => a.PunchIn.length - b.PunchIn.length,
    },
    {
      title: "PunchOut",
      dataIndex: "PunchOut",
      sorter: (a, b) => a.PunchOut.length - b.PunchOut.length,
    },
    {
      title: "Break",
      dataIndex: "Break",
      sorter: (a, b) => a.Break.length - b.Break.length,
    },
    {
      title: "Overtime",
      dataIndex: "Overtime",
      sorter: (a, b) => a.Overtime.length - b.Overtime.length,
    },
  ];
  // Fetch employee attendance data
  useEffect(() => {
    if (user?.emp_id) {
      fetchAttendanceData();
    }
  }, [user, dateRange]);

  const fetchAttendanceData = async () => {
    try {
      // Get attendance logs for the date range
      const attendanceResult = await getEmployeeAttendance({
        employee_id: user.emp_id,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      });

      if (attendanceResult.data?.success) {
        setData(attendanceResult.data.data || []);

        // Get today's attendance for the timesheet card
        const today = attendanceResult.data.data?.find(
          record => dayjs(record.date).isSame(dayjs(), 'day')
        );
        setCurrentAttendance(today);
      }

      // Get attendance summary for statistics
      const summaryResult = await getAttendanceSummary({
        employee_id: user.emp_id,
        start_date: dateRange.start_date,
        end_date: dateRange.end_date
      });

      if (summaryResult.data?.success) {
        const summary = summaryResult.data.data?.[0];
        if (summary) {
          const statsData = [
            {
              title: "Present Days",
              value: summary.total_present || 0,
              valuespan: "days",
              progressWidth: `${((summary.total_present || 0) / 30) * 100}%`,
              progressBarColor: "bg-success"
            },
            {
              title: "Absent Days",
              value: summary.total_absent || 0,
              valuespan: "days",
              progressWidth: `${((summary.total_absent || 0) / 30) * 100}%`,
              progressBarColor: "bg-danger"
            },
            {
              title: "Late Arrivals",
              value: summary.total_late || 0,
              valuespan: "days",
              progressWidth: `${((summary.total_late || 0) / 30) * 100}%`,
              progressBarColor: "bg-warning"
            },
            {
              title: "Early Leaves",
              value: summary.total_early_leave || 0,
              valuespan: "days",
              progressWidth: `${((summary.total_early_leave || 0) / 30) * 100}%`,
              progressBarColor: "bg-info"
            }
          ];
          setStatistics(statsData);
        }
      }

      // Get today's activity
      if (attendanceResult.data?.data) {
        const todayLogs = attendanceResult.data.data.filter(
          record => dayjs(record.date).isSame(dayjs(), 'day')
        );

        const activities = [];
        todayLogs.forEach(log => {
          if (log.first_in) {
            activities.push({
              title: "Punch In",
              time: dayjs(log.first_in).format('hh:mm A')
            });
          }
          if (log.last_out) {
            activities.push({
              title: "Punch Out",
              time: dayjs(log.last_out).format('hh:mm A')
            });
          }
        });
        setTodayActivity(activities);
      }
    } catch (error) {
      console.error('Error fetching attendance data:', error);
    }
  };

  return (
    <>
      <div className="page-wrapper">
        {/* /Page Header */}
        <div className="content container-fluid">
          <Breadcrumbs
            maintitle="Attendance"
            title="Dashboard"
            subtitle="Attendance"
          />

          {/* /Page Header */}
          <div className="row">
            <div className="col-md-4">
              <div className="card punch-status">
                <div className="card-body">
                  {isLoadingAttendance ? (
                    <div className="text-center"><Spin /></div>
                  ) : (
                    <>
                      <h5 className="card-title">
                        Timesheet <small className="text-muted">{dayjs().format('DD MMM YYYY')}</small>
                      </h5>
                      {currentAttendance ? (
                        <>
                          <div className="punch-det">
                            <h6>Punch In at</h6>
                            <p>{currentAttendance.first_in ? dayjs(currentAttendance.first_in).format('ddd, Do MMM YYYY hh:mm A') : 'Not punched in'}</p>
                          </div>
                          <div className="punch-info">
                            <div className="punch-hours">
                              <span>{currentAttendance.working_hours || '0h 0m'}</span>
                            </div>
                          </div>
                          <div className="punch-btn-section">
                            <button
                              type="button"
                              className="btn btn-primary punch-btn"
                              disabled={!currentAttendance.first_in || currentAttendance.last_out}
                            >
                              {currentAttendance.last_out ? 'Punched Out' : 'Punch Out'}
                            </button>
                          </div>
                          <div className="statistics">
                            <div className="row">
                              <div className="col-md-6 col-6 text-center">
                                <div className="stats-box">
                                  <p>Break</p>
                                  <h6>{currentAttendance.break_hours || '0h 0m'}</h6>
                                </div>
                              </div>
                              <div className="col-md-6 col-6 text-center">
                                <div className="stats-box">
                                  <p>Overtime</p>
                                  <h6>{currentAttendance.overtime_hours || '0h 0m'}</h6>
                                </div>
                              </div>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="text-center py-4">
                          <p>No attendance record for today</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card att-statistics">
                <div className="card-body">
                  <h5 className="card-title">Statistics</h5>
                  {isLoadingSummary ? (
                    <div className="text-center"><Spin /></div>
                  ) : (
                    <div className="stats-list">
                      {Array.isArray(statistics) && statistics.length > 0 ? (
                        statistics.map((data, index) => (
                          <div className="stats-info" key={index}>
                            <p>
                              {data.title}{" "}
                              <strong>
                                {data.value} <small>{data.valuespan}</small>
                              </strong>
                            </p>
                            <div className="progress">
                              <div
                                className={`progress-bar ${data.progressBarColor}`}
                                role="progressbar"
                                style={{ width: data.progressWidth }}
                                aria-valuenow={data.progressWidth}
                                aria-valuemin={0}
                                aria-valuemax={100}
                              />
                            </div>
                          </div>
                        ))
                      ) : (
                        <p>No data available</p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card recent-activity">
                <div className="card-body">
                  <h5 className="card-title">Today Activity</h5>
                  {isLoadingAttendance ? (
                    <div className="text-center"><Spin /></div>
                  ) : (
                    <ul className="res-activity-list">
                      {Array.isArray(todayActivity) && todayActivity.length > 0 ? (
                        todayActivity.map((activity, index) => (
                          <li key={index}>
                            <p className="mb-0">{activity.title}</p>
                            <p className="res-activity-time">
                              <i className="fa-regular fa-clock"></i>{" "}
                              {activity.time}
                            </p>
                          </li>
                        ))
                      ) : (
                        <p>No activities available.</p>
                      )}
                    </ul>
                  )}
                </div>
              </div>
            </div>

            <AttendanceEmployeeFilter
              onFilterChange={handleFilterChange}
              initialFilters={dateRange}
            />
            <div className="row">
              <div className="col-lg-12">
                <div className="table-responsive">
                  <Table
                    columns={columns}
                    dataSource={userElements?.length > 0 ? userElements : []}
                    className="table-striped"
                    rowKey={(record) => record.id}
                    loading={isLoadingAttendance}
                    pagination={{
                      pageSize: 10,
                      showSizeChanger: true,
                      showTotal: (total) => `Total ${total} records`
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AttendanceEmployee;
